import { TFunction } from 'i18next';
import { ExternalLink, Wand2 } from 'lucide-react';
import { type ShadowSystem } from '@bliks-io/contracts';
import {
  createActionsColumn,
  createColumnHeader,
  createDateColumn,
  createUserCardColumn,
  renderFallback,
  TruncatedText,
  type TombstoneLookup,
  type DataTableColumnDef
} from '@bliks-io/ui';

/**
 * Columns for the Shadow Systems table. The full-meta layout favours column
 * visibility toggling (enabled on the table) over a separate detail view.
 * `onResolve` opens the resolve dialog for a row (merge into / promote to a
 * System).
 */
export function createShadowSystemColumns(
  t: TFunction,
  onResolve: (shadowSystem: ShadowSystem) => void,
  userLookup?: TombstoneLookup
): DataTableColumnDef<ShadowSystem>[] {
  // Static keys (no dynamic interpolation) so the translation checker can verify them.
  const col = {
    system: t('shadowSystems.columns.system'),
    category: t('shadowSystems.columns.category'),
    url: t('shadowSystems.columns.url'),
    openLink: t('shadowSystems.columns.openLink'),
    occurrences: t('shadowSystems.columns.occurrences'),
    suggestedBy: t('shadowSystems.columns.suggestedBy'),
    submittedAt: t('shadowSystems.columns.submittedAt'),
    reviewedBy: t('shadowSystems.columns.reviewedBy'),
    reviewedAt: t('shadowSystems.columns.reviewedAt'),
    rejectionReason: t('shadowSystems.columns.rejectionReason'),
    source: t('shadowSystems.columns.source')
  };

  return [
    {
      id: 'name',
      accessorFn: (row) => row.payload.name,
      meta: { label: col.system, filterType: 'text' },
      header: createColumnHeader(col.system),
      cell: ({ row }) => {
        const { name, description } = row.original.payload;
        return (
          <div className="min-w-[160px]">
            <div className="font-medium">{name}</div>
            {description && (
              <TruncatedText
                text={description}
                className="max-w-[240px] text-xs text-muted-foreground"
              />
            )}
          </div>
        );
      },
      enableColumnFilter: true
    },
    {
      id: 'category',
      accessorFn: (row) => row.payload.category ?? '',
      meta: { label: col.category, filterType: 'text' },
      header: createColumnHeader(col.category),
      cell: ({ row }) => renderFallback(row.original.payload.category),
      enableColumnFilter: true
    },
    {
      id: 'url',
      meta: { label: col.url },
      header: createColumnHeader(col.url),
      cell: ({ row }) => {
        const url = row.original.payload.url;
        if (!url) return renderFallback(url);
        return (
          <a
            href={url}
            target="_blank"
            rel="noreferrer"
            aria-label={col.openLink}
            className="inline-flex items-center text-primary hover:underline"
          >
            <ExternalLink className="h-4 w-4" />
          </a>
        );
      },
      enableSorting: false
    },
    {
      accessorKey: 'occurrenceCount',
      meta: { label: col.occurrences },
      header: createColumnHeader(col.occurrences),
      cell: ({ row }) => (
        <span className="font-medium tabular-nums">{row.original.occurrenceCount}</span>
      ),
      enableSorting: true
    },
    createUserCardColumn<ShadowSystem>('submittedBy', col.suggestedBy, {
      variant: 'with-email',
      size: 'sm',
      enableFiltering: true,
      userLookup
    }),
    createDateColumn<ShadowSystem>('submittedAt', col.submittedAt),
    createUserCardColumn<ShadowSystem>('reviewedBy', col.reviewedBy, {
      variant: 'with-email',
      size: 'sm',
      userLookup
    }),
    createDateColumn<ShadowSystem>('reviewedAt', col.reviewedAt),
    {
      id: 'rejectionReason',
      accessorFn: (row) => row.rejectionReason ?? '',
      meta: { label: col.rejectionReason },
      header: createColumnHeader(col.rejectionReason),
      cell: ({ row }) => (
        <TruncatedText text={row.original.rejectionReason} className="max-w-[260px]" />
      ),
      enableSorting: false
    },
    {
      id: 'source',
      accessorFn: (row) => row.sourceRecordingStepLabel ?? row.sourceProcessLabel ?? '',
      meta: { label: col.source },
      header: createColumnHeader(col.source),
      cell: ({ row }) => (
        <TruncatedText
          text={row.original.sourceRecordingStepLabel ?? row.original.sourceProcessLabel}
          className="max-w-[220px]"
        />
      ),
      enableSorting: false
    },
    createActionsColumn<ShadowSystem>({
      getCustomActions: (row) => [
        {
          label: t('shadowSystems.actions.resolve'),
          icon: <Wand2 className="h-4 w-4" />,
          onClick: () => onResolve(row)
        }
      ]
    })
  ];
}
